import { useEffect, useRef } from "react";
import { Badge } from "@/components/ui/Badge";
import { TimecodeChip } from "@/components/ui/TimecodeChip";
import { CloseIcon } from "./icons";

// ════════════════════════════════════════════════════════════════════════
// ComposerDrawer — NEW (Coach Review v2 layout).
// ────────────────────────────────────────────────────────────────────────
// The bottom slide-up drawer that hosts the note / clip composer.
// Ports the standalone reference's composer drawer: it's opened on
// demand ("Mark moment" / "Add details" after "Make clip" enters
// clip mode / M), not always-visible — the v1
// layout kept the composer permanently mounted in the canvas's
// toolbar rail.
//
// The drawer is `position: fixed` along the bottom edge, capped near
// the viewport height with its own scroll. The header carries a kind badge (NEW
// NOTE / NEW CLIP) + a timecode chip (the playhead for a note, the
// clip range for a clip) + a Close button. The body is the caller's
// composer node.
//
// Esc closes (the parent also clears clip mode on close where
// relevant). Reuses <Badge> / <TimecodeChip> + the v2 inline icons —
// no new tokens, no rebuilt primitives.
// ════════════════════════════════════════════════════════════════════════

export type ComposerDrawerProps = {
  /** "note" → coaching note / observation; "clip" → video clip. */
  kind: "note" | "clip";
  /** Notes created from the tactical board use the observation title. */
  observation?: boolean;
  /** Existing feedback opens in edit mode; new feedback opens in create mode. */
  editing?: boolean;
  /** Canonical Resource details reuse the same drawer. */
  title?:string;
  /** Timecode line shown in the header — the playhead readout for a
   * note, or the "IN → OUT · DUR" range for a clip. */
  timecodeLabel: string;
  /** Match name or Tactical board, shown beside the time/range chip. */
  contextLabel: string;
  /** Close the drawer (the X button + Esc both call this). */
  onClose: () => void;
  /** Extra header content between the timecode chip and Close —
   * Coach Review puts a compact source toggle here, because the
   * drawer overlays the page's tab row (#366) and flipping the
   * source mid-compose is a designed flow. */
  headerSlot?: React.ReactNode;
  /** Optional edit-only selector placed beside the context chips. */
  contextSlot?: React.ReactNode;
  /** The composer body — the note or clip composer stack. */
  children: React.ReactNode;
};

export function ComposerDrawer({
  kind,
  observation = false,
  editing = false,
  title:explicitTitle,
  timecodeLabel,
  contextLabel,
  onClose,
  headerSlot,
  contextSlot,
  children,
}: ComposerDrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Esc closes the drawer. Doesn't preventDefault — the review
  // keyboard hook also reacts to Esc (disarm tools) and both landing
  // is fine. Scoped to keydown while the drawer is mounted.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || document.querySelector('[role="alertdialog"],[data-coach-modal-active="true"],[role="listbox"]')) return;
      if (document.querySelector('[data-testid="feedback-player-modal"]')) {
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Move focus into the drawer on open so keyboard users land inside
  // it; the parent restores focus to the trigger on close.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const title = explicitTitle ?? (editing
    ? "Edit feedback details"
    : kind === "clip"
      ? "New clip"
      : observation
        ? "New observation"
        : "New note");

  return (
    <>
      <div className="review-composer-backdrop" aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        data-kind={kind}
        data-observation={observation || undefined}
        data-testid="coach-review-composer-drawer"
        className="review-composer-drawer"
      >
        <div className="review-composer-header">
          <h2>{title}</h2>
          <div className="review-composer-header-actions">
            {headerSlot}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              title="Close (Esc)"
              data-testid="coach-review-composer-drawer-close"
              className="review-composer-close"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        <div className="review-composer-body">
          <div className="review-composer-context">
            <div className="review-composer-context-chips">
              <Badge variant="muted">{contextLabel}</Badge>
              <TimecodeChip>{timecodeLabel}</TimecodeChip>
            </div>
            {contextSlot ? (
              <div className="review-composer-context-tools">{contextSlot}</div>
            ) : null}
          </div>
          {children}
        </div>
      </div>
    </>
  );
}
